// @refresh reset
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef, ReactNode } from 'react';
import { processQueue, loadQueue, addQueueItem, removeQueueItem, extractQueueBlobs, removeQueueBlobs, QueueItemLockTimeoutError } from '../services/queueService';
import type { QueueItem, QueueProcessOutcome } from '../interfaces/queue';
import { requiresManualConfirmation } from '../interfaces/queue';
import { useAuth } from './AuthContext';

import type { SyncState } from '../types/sync-state';
import type { QueueContextType } from '../interfaces/queue';

const QueueContext = createContext<QueueContextType>({
  items: [],
  addToQueue: async () => undefined,
  removeFromQueue: async () => {},
  forgetQueueItem: () => {},
  confirmPendingItem: async () => undefined,
  isProcessing: false,
  isOnline: true,
  syncQueue: async () => {},
  syncStatus: {
    pending: 0,
    lastSync: null,
    error: null,
    state: 'idle'
  }
});

export const QueueProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<QueueItem[]>([]);
  const isInitializedRef = useRef<boolean>(false); // ✅ useRef Guard statt useState (überlebt StrictMode)
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [syncStartTime, setSyncStartTime] = useState<number | null>(null);
  const [lastResult, setLastResult] = useState<{ successful: number; failed: number } | undefined>(undefined);
  const { isAuthenticated } = useAuth();

  // Ref-Spiegel von `items` für processQueueItems' Default-Parameter, damit die
  // Callback-Identität von addToQueue/processQueueItems NICHT von `items` abhängt.
  // Grund: Ein Consumer (z.B. ProjektdokumentationWizard-Autosave), der addToQueue
  // in einem useEffect-Dependency-Array hat, würde sonst bei jedem Enqueue (das
  // items ändert) eine neue addToQueue-Referenz sehen → Effect feuert erneut →
  // Endlosschleife, die pro Sekunde neue Queue-Items anlegt (Bug #1070).
  const itemsRef = useRef<QueueItem[]>(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Ref-Spiegel von `isProcessing`, aus demselben Grund wie `itemsRef`: `isProcessing`
  // kippt bei JEDER Queue-Aktivität irgendwo in der App (Bild-Upload, andere Wizards,
  // Online-Reconnect), nicht nur bei diesem einen Enqueue. War `isProcessing` weiterhin
  // ein Dependency von processQueueItems/addToQueue, bekamen beide bei jedem fremden
  // Processing-Zyklus eine neue Referenz → derselbe Effect-Refire-Bug wie mit `items`,
  // nur ausgelöst von anderswo. Über setProcessing wird der Ref synchron mit dem State
  // aktualisiert (kein useEffect-Versatz), damit die Gate-Prüfung immer den aktuellen
  // Wert sieht.
  const isProcessingRef = useRef(false);
  const setProcessing = useCallback((value: boolean) => {
    isProcessingRef.current = value;
    setIsProcessing(value);
  }, []);

  // Auto-Recovery nach 60s wenn State auf 'syncing' hängen bleibt
  useEffect(() => {
    if (syncState === 'syncing' && syncStartTime) {
      const timeoutId = setTimeout(() => {
        const elapsed = Date.now() - syncStartTime;
        if (elapsed >= 60000) { // 60 Sekunden
          console.error('Sync timeout - auto-recovery nach 60s');
          setSyncState('failed');
          setSyncError('⚠ Sync-Timeout – Bitte erneut versuchen');
          setProcessing(false);
          setSyncStartTime(null);
        }
      }, 60000);

      return () => clearTimeout(timeoutId);
    }
  }, [syncState, syncStartTime, setProcessing]);

  // Cleanup bei Component-Unmount
  useEffect(() => {
    return () => {
      if (syncState === 'syncing') {
        console.log('QueueContext unmount - reset sync state');
        setSyncState('idle');
        setProcessing(false);
      }
    };
  }, [syncState, setProcessing]);

  // Queue aus IndexedDB laden (OHNE State-Update - nur Return)
  const loadQueueFromIndexedDB = async () => {
    try {
      const loadedItems = await loadQueue();
      // ❌ NICHT setItems(loadedItems) hier - das verursacht Badge-Flackern!
      // State-Update muss NACH Processing passieren
      return loadedItems;
    } catch (error) {
      console.error('Fehler beim Laden der Queue:', error);
      return [];
    }
  };

  // Online/Offline-Status überwachen
  useEffect(() => {
    const handleOnlineStatus = async () => {
      setIsOnline(true);
      
      // ✅ FIX 8: Auth-Check ENTFERNT (gleicher Grund wie bei initializeQueue)
      // Queue-Items sollten verarbeitet werden sobald online, unabhängig von Auth-Status
      if (isInitializedRef.current) {
        const loadedItems = await loadQueueFromIndexedDB();
        // Items, die explizite Nutzerbestätigung brauchen (siehe requiresManualConfirmation),
        // dürfen hier NICHT automatisch mitverarbeitet werden - sie bleiben unverändert in
        // der Queue liegen, bis confirmPendingItem sie explizit anstößt.
        const autoItems = loadedItems.filter((item) => !requiresManualConfirmation(item.type));

        if (autoItems.length > 0) {
          console.log('[ONLINE] Processing queue items before state update...');

          // ✅ FIX 7: State sofort auf die (unveränderten) bestätigungspflichtigen Items setzen
          setItems(loadedItems.filter((item) => requiresManualConfirmation(item.type)));

          try {
            const { successful, failed } = await processQueue(autoItems);

            // Nach Processing: Queue neu laden
            const finalQueue = await loadQueue();
            setItems(finalQueue);

            if (failed.length > 0) {
              setSyncState('failed');
            } else if (successful.length > 0) {
              setSyncState('completed');
              setTimeout(() => setSyncState('idle'), 3000);
            }
          } catch (error) {
            console.error('[ONLINE] Processing error:', error);
            setItems(loadedItems); // Fallback
          }
        } else if (loadedItems.length > 0) {
          setItems(loadedItems);
        }
      }
    };

    const handleOfflineStatus = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOfflineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOfflineStatus);
    };
  }, [isAuthenticated]);

  // Queue beim Start laden - NUR EINMAL beim Mount
  // ✅ FIX: Await processQueue BEFORE setting items (verhindert Race-Condition bei Page-Reload)
  // ✅ FIX: useRef-Guard verhindert doppelte Initialisierung (React StrictMode remountet 2x)
  // ✅ FIX 7: SOFORTIGES State-Reset auf [] während Processing (Badge zeigt NIE alte Items)
  // Warum: Badge soll NIE den Zwischenstand sehen (weder 0→1 noch 1→0 Flackern)
  useEffect(() => {
    // ✅ Guard: Verhindert mehrfache Initialisierung bei StrictMode/Remounts
    if (isInitializedRef.current) {
      console.log('[QUEUE-INIT] Already initialized, skipping');
      return;
    }
    
    const initializeQueue = async () => {
      console.log('[QUEUE-INIT] Starting initialization...');
      
      try {
        const loadedItems = await loadQueue();
        console.log('[QUEUE-INIT] Loaded items:', loadedItems.length);
        console.log('[QUEUE-INIT] Auth status:', { isAuthenticated, isOnline });
        
        // ✅ FIX 8: Processing NUR wenn online (Auth-Check ENTFERNT!)
        // Warum entfernt: Bei Page-Load ist isAuthenticated oft noch FALSE
        // (Auth-Context mountet parallel). Items sollten trotzdem verarbeitet werden.
        // API-Calls sind bereits auth-protected, kein Security-Risk.
        // Bestätigungspflichtige Items (siehe requiresManualConfirmation) werden hier NICHT
        // automatisch verarbeitet - sie bleiben unverändert in der Queue liegen.
        const autoItems = loadedItems.filter((item) => !requiresManualConfirmation(item.type));

        if (autoItems.length > 0 && isOnline) {
          console.log('[QUEUE-INIT] Processing items before state update...');

          // ✅ FIX 7: State auf die (unveränderten) bestätigungspflichtigen Items setzen -
          // Badge zeigt während des Processings nur diese, nicht die auto-verarbeiteten.
          setItems(loadedItems.filter((item) => requiresManualConfirmation(item.type)));

          const { successful, failed } = await processQueue(autoItems);
          console.log('[QUEUE-INIT] Processing done. Successful:', successful.length, 'Failed:', failed.length);

          // Nach Processing: Queue neu laden (sollte leer sein oder nur failed/bestätigungspflichtige items)
          const finalQueue = await loadQueue();
          console.log('[QUEUE-INIT] Final queue length:', finalQueue.length);

          setItems(finalQueue);

          // Sync-Status updaten
          if (failed.length > 0) {
            setSyncState('failed');
            setSyncError(`${failed.length} von ${successful.length + failed.length} Uploads fehlgeschlagen`);
          } else if (successful.length > 0) {
            setSyncState('completed');
            setTimeout(() => setSyncState('idle'), 3000);
          }
        } else {
          // Keine automatisch verarbeitbaren Items ODER offline → direkt setzen
          console.log('[QUEUE-INIT] No processing needed, setting items directly');
          console.log('[QUEUE-INIT] Reason:', loadedItems.length === 0 ? 'no items' : 'offline or manual-confirmation-only');
          setItems(loadedItems);
        }
        
        isInitializedRef.current = true;
        console.log('[QUEUE-INIT] Initialization complete');
      } catch (error) {
        console.error('[QUEUE-INIT] Error during initialization:', error);
        isInitializedRef.current = true; // Auch bei Error als initialized markieren
      }
    };
    
    initializeQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ← LEER! Nur beim Mount, nicht bei Auth/Online-Changes

  // ServiceWorker-Updates überwachen
  useEffect(() => {
    const handleServiceWorkerMessage = async (event: MessageEvent) => {
      const data = event.data;
      
      // ✅ FIX: Bei erfolgreicher Synchronisierung Queue laden OHNE State-Update
      // ServiceWorker hat bereits synchronisiert, wir laden nur finalen State
      if (data.type === 'SYNC_COMPLETED') {
        const finalQueue = await loadQueue();
        setItems(finalQueue);
        setLastSync(new Date());
      }
    };
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }
    
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  // Queue-Verarbeitung mit State Machine + Timeout-Tracking
  //
  // Gibt das reale {successful, failed}-Ergebnis zurück, sofern hier tatsächlich sofort
  // verarbeitet wurde — `undefined`, wenn die Verarbeitung aus irgendeinem Grund nicht
  // sofort lief (Guard-Klausel oben, oder eine Exception im try-Block). Aufrufer wie
  // ReworkCommentThread.addToQueue-Consumer brauchen dieses Ergebnis, um den Ausgang EINES
  // bestimmten, gerade selbst enqueuten Items sofort und eindeutig zu kennen — ein reiner
  // Blick auf `items`/ein globales Event kann nicht zuverlässig zwischen "noch nicht
  // registriert" und "bereits fertig verarbeitet" unterscheiden (PR-Review-Finding zu #1090:
  // genau diese Mehrdeutigkeit führte zu einem False-Positive-Fehlschlag direkt nach dem
  // Absenden, weil das frisch enqueute Item im ersten Render-Zyklus noch nicht in `items`
  // auftaucht, obwohl es gerade erst losläuft statt schon fertig zu sein).
  const processQueueItems = useCallback(async (
    queueItems?: QueueItem[]
  ): Promise<QueueProcessOutcome | undefined> => {
    const itemsToProcess = queueItems || itemsRef.current;

    if (isProcessingRef.current || !isOnline || !isAuthenticated || itemsToProcess.length === 0) {
      return undefined;
    }

    setProcessing(true);
    setSyncState('syncing');
    setSyncStartTime(Date.now()); // Zeitstempel für 60s Timeout

    try {
      const { successful, failed, permanentlyFailed } = await processQueue(itemsToProcess);

      // Ergebnis speichern für UI-Feedback
      setLastResult({
        successful: successful.length,
        failed: failed.length
      });

      // ✅ FIX: Vereinfachte setState-Strategie (nur DB-Reload, kein Optimistic Update)
      // Warum: Doppelte setState-Aufrufe über await-Boundary erzeugen zwei separate
      // Render-Batches (React 18). Badge-Component kann Zwischenstand sehen.
      // Bei 500ms Mock-Upload-Delay bringt Optimistic Update keinen UX-Gewinn.
      const updatedQueue = await loadQueue();
      setItems(updatedQueue);

      setLastSync(new Date());
      setSyncStartTime(null); // Reset nach erfolgreichem Sync

      // State je nach Ergebnis setzen
      if (failed.length > 0) {
        setSyncState('failed');
        setSyncError(`${failed.length} von ${successful.length + failed.length} Uploads fehlgeschlagen`);
        setSyncStartTime(null);
      } else {
        setSyncState('completed');
        setSyncError(null);
        setSyncStartTime(null);

        // Nach 3 Sekunden zurück zu 'idle'
        setTimeout(() => setSyncState('idle'), 3000);
      }

      return { successful, failed, permanentlyFailed };
    } catch (error) {
      console.error('Fehler bei der Queue-Verarbeitung:', error);
      setSyncState('failed');
      setSyncError('Synchronisierungsfehler');
      setSyncStartTime(null);
      return undefined;
    } finally {
      setProcessing(false);
    }
  }, [isOnline, isAuthenticated, setProcessing]);

  // Manueller Sync der Queue (für UI-Aktionen)
  const syncQueue = useCallback(async () => {
    if (isProcessingRef.current || !isOnline) return;
    const loadedItems = await loadQueue();
    // "Sync jetzt" ist ein genereller "alles nachholen"-Trigger, keine gezielte Bestätigung -
    // bestätigungspflichtige Items (siehe requiresManualConfirmation) bleiben davon
    // ausgenommen, genau wie bei den anderen automatischen Verarbeitungsstellen.
    const autoItems = loadedItems.filter((item) => !requiresManualConfirmation(item.type));
    if (autoItems.length === 0) return;

    // Retries für blockierte Items zurücksetzen, damit der User sie explizit nochmal versuchen kann
    const itemsToSync = await Promise.all(
      autoItems.map(async (item) => {
        if (item.retries >= 5) {
          // `permanentFailureNotified` (queueService.processQueue) explizit mit zurücksetzen,
          // nicht nur `retries` (Re-Review-Finding, Anh360Volt): scheitert dieser manuelle Retry
          // erneut bis zur Erschöpfung, würde das Flag sonst noch von der VORHERIGEN
          // Erschöpfung stammen und die erneute Permanent-Fehler-Meldung stumm unterdrücken.
          const reset = { ...item, retries: 0, data: { ...item.data, permanentFailureNotified: false } };
          await addQueueItem(reset);
          return reset;
        }
        return item;
      })
    );

    setItems([
      ...itemsToSync,
      ...loadedItems.filter((item) => requiresManualConfirmation(item.type)),
    ]);
    setProcessing(true);
    setSyncState('syncing');
    setSyncStartTime(Date.now());
    try {
      const { successful, failed } = await processQueue(itemsToSync);
      setLastResult({ successful: successful.length, failed: failed.length });
      const updatedQueue = await loadQueue();
      setItems(updatedQueue);
      setLastSync(new Date());
      setSyncStartTime(null);
      if (failed.length > 0) {
        setSyncState('failed');
        setSyncError(`${failed.length} von ${successful.length + failed.length} Uploads fehlgeschlagen`);
      } else {
        setSyncState('completed');
        setSyncError(null);
        setTimeout(() => setSyncState('idle'), 3000);
      }
    } catch (err) {
      setSyncState('failed');
      setSyncError('Sync fehlgeschlagen');
    } finally {
      setProcessing(false);
      setSyncStartTime(null);
    }
  }, [isOnline, setProcessing]);

  // Element zur Queue hinzufügen
  const addToQueue = useCallback(async (
    type: string,
    data: any,
    options?: { resolveData?: (currentData: unknown) => unknown }
  ) => {
    const queueKey =
      data && typeof data === 'object' && typeof data.queueKey === 'string' && data.queueKey.trim() !== ''
        ? data.queueKey.trim()
        : null;
    const itemId = queueKey || Date.now().toString();
    // Bei einem queueKey überschreibt addQueueItem das bestehende IndexedDB-Item unter
    // derselben id. Ohne den bisherigen retries-Zähler mitzunehmen, würde jeder Re-Save
    // (z.B. Autosave, die bei anhaltendem Backend-Fehler denselben Payload erneut
    // enqueued) den Exhaustion-Schutz aus queueService (Abbruch ab retries>=5) aushebeln
    // und damit unbegrenzt oft denselben fehlschlagenden Request auslösen.
    const existingItem = queueKey ? itemsRef.current.find((item) => item.id === queueKey) : undefined;
    // Große Foto-/Datei-Payloads (document_upload/image_upload) einmalig in den separaten
    // Blob-Store auslagern, bevor sie in IndexedDB landen - für alle anderen Typen unverändert
    // (siehe extractQueueBlobs-Doku, #1216). Dadurch trägt newItem.data ab hier bereits die
    // leichte Blob-Referenz-Form, ein späterer Retry (queueService.processQueue) schreibt somit
    // nie wieder die vollen Base64-Daten neu.
    const extractedData = await extractQueueBlobs(type, data);
    const newItem: QueueItem = {
      id: itemId,
      type,
      data: extractedData,
      retries: existingItem?.retries ?? 0,
      createdAt: new Date()
    };

    // In IndexedDB speichern
    // `resolveData` (siehe QueueContextType-Doku, PR-Review-Finding 10. Re-Review zu #976/#422)
    // liest den aktuell persistierten Stand erst INNERHALB des Locks für diese ID - anders als
    // ein vom Aufrufer VORHER ausgeführtes `loadQueue()` sieht das damit garantiert auch einen
    // noch nicht abgeschlossenen vorherigen Schreibvorgang für dieselbe ID (Lock-Timeout).
    let storedItem = newItem;
    try {
      storedItem = await addQueueItem(
        newItem,
        options?.resolveData
          ? (current) => ({
              ...newItem,
              retries: current?.retries ?? newItem.retries,
              data: options.resolveData!(current?.data ?? null),
            })
          : undefined
      );
    } catch (error) {
      // AUSNAHME (PR-Review-Finding, 3. Re-Review zu #976/#422): Ein QueueItemLockTimeoutError
      // ist kein definitiver Fehlschlag - das zugrunde liegende setItem(newItem) kann im
      // Hintergrund noch erfolgreich abschließen (siehe withQueueItemLock-Doku). Blob-Rollback
      // (wie unten für echte Fehler) darf deshalb hier NICHT passieren - das verspätete setItem
      // würde sonst ein Queue-Item mit bereits gelöschtem blobRef/imageDataRef schreiben.
      //
      // Den Fehler selbst geben wir aber wieder an den Aufrufer weiter (anders als zuvor): der
      // unbestimmte Ausgang bedeutet für unterschiedliche Aufrufer etwas anderes und lässt sich
      // deshalb nicht zentral hier auflösen (Re-Review-Finding). document_upload/image_upload-
      // Aufrufer in PhaseScreen behandeln QueueItemLockTimeoutError inzwischen explizit (kein
      // Rollback, kein Retry mit neuer ID); queueVaillantSerialRegisterFallback braucht den
      // geworfenen Fehler dagegen, um seinen Ambiguitätsmarker (vaillantEnqueueFailure) NICHT
      // zu löschen - ein still aufgelöstes addToQueue sähe dort wie eine bestätigte
      // Registrierung aus, obwohl setItem noch offen ist.
      if (error instanceof QueueItemLockTimeoutError) {
        console.warn(
          `addToQueue: Speichern für ${itemId} hängt noch (Lock-Timeout) - Aufrufer entscheidet selbst, wie mit dem unbestimmten Ausgang umzugehen ist.`
        );
        // PR-Review-Finding (9. Re-Review zu #976/#422, ReworkCommentThread): der Aufrufer
        // bekommt hier nur den unbestimmten Timeout-Reject - schließt das zugrunde liegende
        // setItem später doch noch ab, erreicht diese Funktion den normalen Erfolgspfad unten
        // (sofortiges Verarbeiten bzw. das State-Update im else-Zweig) NIE MEHR, weil der
        // Aufruf hier bereits per throw verlassen wurde. Ohne Nachführung bliebe das Item
        // unsichtbar in IndexedDB liegen, bis irgendeine ANDERE Queue-Aktivität zufällig einen
        // Reload auslöst (Online-Event, Service-Worker-Message, ein weiterer addToQueue-Aufruf) -
        // ein Aufrufer wie ReworkCommentThread, der ausschließlich `items`/das
        // project-status-refresh-Event beobachtet, hätte den späten Schreibvorgang sonst nie
        // erfahren und den optimistischen Eintrag fälschlich als endgültig gescheitert behandelt.
        // `error.settled` löst erst auf, wenn `fn()` TATSÄCHLICH fertig ist (siehe
        // QueueItemLockTimeoutError-Doku) - dann holen wir denselben Abschluss nach, den ein
        // rechtzeitiges addQueueItem hier unten ausgelöst hätte.
        error.settled
          .then(async () => {
            const updatedQueue = await loadQueue();
            const persisted = updatedQueue.find((queued) => queued.id === itemId);
            // requiresManualConfirmation-Typen (z.B. vaillant_serial_register) dürfen auch nach
            // einem späten Abschluss nicht automatisch verarbeitet werden - der Ambiguitätsmarker
            // beim jeweiligen Aufrufer bleibt für sie die einzige Bestätigungsquelle.
            const processed = persisted && !requiresManualConfirmation(type)
              ? await processQueueItems([persisted])
              : undefined;
            // processQueueItems aktualisiert `items` bereits selbst, sobald es tatsächlich
            // verarbeitet hat (seine eigenen Guard-Klauseln greifen z.B. bei zwischenzeitlichem
            // Offline-Wechsel oder einer parallel laufenden anderen Verarbeitung) - sonst hier den
            // reinen Lesezustand nachziehen, damit Badge/Liste den späten Schreibvorgang
            // wenigstens sichtbar machen.
            if (!processed) {
              setItems(updatedQueue);
            }
          }, async (settleError: unknown) => {
            // main/#1216: Erst ein tatsächlich fehlgeschlagener Write erlaubt Blob-Cleanup.
            // Der zweite then-Handler gilt nur für den Write; Fehler der nachgelagerten
            // Verarbeitung dürfen keine bereits persistierten Blob-Referenzen löschen.
            console.warn(`addToQueue: Verspätete Operation für ${itemId} ist endgültig fehlgeschlagen:`, settleError);
            await removeQueueBlobs(type, extractedData);
          })
          .catch((reconcileError: unknown) => {
            console.error('Nachführung oder Blob-Cleanup nach Queue-Timeout fehlgeschlagen:', itemId, reconcileError);
          });
        throw error;
      }
      // Die Blobs oben wurden bereits erfolgreich geschrieben, aber das leichte Metadaten-
      // Item selbst konnte nicht persistiert werden (z.B. Quota erst hier erschöpft) - ohne
      // Rollback blieben diese Blobs für immer verwaist, ohne dass sie je einem Queue-Item
      // zugeordnet werden (Code-Review-Finding #1216).
      await removeQueueBlobs(type, extractedData);
      throw error;
    }

    // ❌ ENTFERNT: Optimistisches State-Update verursacht Badge-Flackern
    // setItems(prevItems => [...prevItems, newItem]);
    // Warum entfernt: Bei schnellem Upload sieht Badge "1" zwischen optimistic
    // update und finalem DB-Reload. processQueueItems macht bereits finales
    // setItems(updatedQueue), deshalb ist optimistic update überflüssig.

    // Wenn online und authentifiziert, sofort verarbeiten - AUSSER der Typ verlangt explizite
    // Nutzerbestätigung (siehe requiresManualConfirmation): so ein Item bleibt unverändert in
    // der Queue liegen, bis confirmPendingItem es explizit anstößt (#1199, Code-Review-
    // Finding: ein automatischer Retry könnte eine bereits erfolgreich durchgelaufene,
    // nicht-idempotente externe Aktion ein zweites Mal auslösen).
    // ✅ FIX: processQueueItems macht finales setItems(updatedQueue)
    if (isOnline && isAuthenticated && !isProcessingRef.current && !requiresManualConfirmation(type)) {
      // Ergebnis an den Aufrufer zurückgeben (siehe processQueueItems-Doku) — `undefined`
      // bedeutet hier "sofortige Verarbeitung ist aus einem anderen Grund nicht gelaufen",
      // fällt also faktisch in denselben "noch offen, später beobachten"-Fall wie der
      // else-Zweig unten.
      //
      // `storedItem` statt `newItem` (PR-Review-Finding, 11. Re-Review zu #976/#422): bei einem
      // `resolveData`-Merge (z.B. MaintenanceProtocols.uploadFiles) weicht das tatsächlich in
      // IndexedDB gespeicherte Item von `newItem` ab (enthält z.B. eine bereits wartende Ladung
      // A zusätzlich zur soeben hinzugefügten B). `newItem` hier zu verarbeiten würde nur B
      // hochladen; ein anschließend erfolgreicher processQueue-Abschluss entfernt die ID aber
      // bereits, wodurch der gemergte A+B-Stand verloren ginge, ohne dass A je hochgeladen wurde.
      return await processQueueItems([storedItem]);
    } else {
      // Nur offline Background Sync registrieren (nicht wenn sofort verarbeitet wird).
      // Bestätigungspflichtige Typen (siehe requiresManualConfirmation) werden davon
      // ausgenommen (Code-Review-Finding, Re-Review): dieser Sync-Tag-Mechanismus greift für
      // sie ohnehin nicht (kein Route-Match in sw.js) und das Warten auf
      // navigator.serviceWorker.ready würde nur unnötig die Zeit verlängern, bis das
      // Pending-Item über setItems unten im UI sichtbar wird.
      // AB-Aufgaben benötigen den angemeldeten Benutzer und werden beim Reconnect
      // durch diese Queue synchronisiert; der Service Worker hat dafür keinen Handler.
      if (type !== 'order_task_complete' && !requiresManualConfirmation(type) && 'serviceWorker' in navigator && 'SyncManager' in window) {
        try {
          const registration = await navigator.serviceWorker.ready;
          if ('sync' in registration) {
            const syncTag = type === 'image_upload' ? 'imageUploads' : 'checklistUpdates';
            await (registration as any).sync.register(syncTag);
          }
        } catch (error) {
          console.warn('Background Sync nicht verfügbar:', error);
        }
      }
      // ✅ Wenn offline oder processing läuft: Manuell State updaten
      const updatedQueue = await loadQueue();
      setItems(updatedQueue);
    }
  }, [isOnline, isAuthenticated, processQueueItems]);

  // Verarbeitet EIN bestimmtes Queue-Item explizit auf Nutzeranfrage - unabhängig davon, ob
  // sein Typ von der automatischen Verarbeitung ausgenommen ist (siehe
  // requiresManualConfirmation). Einziger Weg, ein solches Item überhaupt zu verarbeiten.
  const confirmPendingItem = useCallback(async (id: string): Promise<QueueProcessOutcome | undefined> => {
    if (isProcessingRef.current || !isOnline) return undefined;

    const loadedItems = await loadQueue();
    const target = loadedItems.find((item) => item.id === id);
    if (!target) return undefined;

    setProcessing(true);
    setSyncState('syncing');
    setSyncStartTime(Date.now());
    try {
      // Code-Review-Finding (P1): processQueue verwirft Items mit retries >= 5 sofort als
      // permanentlyFailed, OHNE den eigentlichen Request überhaupt zu versuchen (Exhaustion-
      // Schutz gegen endloses automatisches Retrying). Für bestätigungspflichtige Typen gibt es
      // aber keinen anderen Weg, diesen Zähler zurückzusetzen (syncQueue schließt sie explizit
      // aus) - eine explizite Nutzerbestätigung ist per Definition kein automatischer Retry und
      // muss den Request immer tatsächlich ausführen, sonst bliebe der Banner nach 5
      // fehlgeschlagenen Versuchen für immer hängen, ohne dass der Nutzer das beheben könnte.
      // Innerhalb des try-Blocks (Code-Review-Finding, P2): ein Fehler beim Persistieren
      // dieses Resets (z.B. Speicherplatz voll) darf confirmPendingItem nicht ungefangen
      // werfen lassen - das würde beim Aufrufer (handleConfirmVaillantRegistration) in einer
      // unbehandelten Promise-Rejection ohne Fehler-Toast enden.
      // `permanentFailureNotified` explizit mit zurücksetzen (Re-Review-Finding, Anh360Volt) -
      // siehe gleichwertiger Reset in syncQueue oben.
      const itemToProcess = target.retries >= 5
        ? { ...target, retries: 0, data: { ...target.data, permanentFailureNotified: false } }
        : target;
      if (itemToProcess !== target) {
        await addQueueItem(itemToProcess);
      }
      const result = await processQueue([itemToProcess]);
      setLastResult({ successful: result.successful.length, failed: result.failed.length });
      const updatedQueue = await loadQueue();
      setItems(updatedQueue);
      setLastSync(new Date());
      if (result.failed.length > 0) {
        setSyncState('failed');
        setSyncError('Bestätigung fehlgeschlagen');
      } else {
        setSyncState('completed');
        setSyncError(null);
        setTimeout(() => setSyncState('idle'), 3000);
      }
      return result;
    } catch (error) {
      console.error('[QueueContext] confirmPendingItem fehlgeschlagen:', error);
      setSyncState('failed');
      setSyncError('Bestätigung fehlgeschlagen');
      return undefined;
    } finally {
      setProcessing(false);
      setSyncStartTime(null);
    }
  }, [isOnline, setProcessing]);

  // Element aus der Queue entfernen
  const removeFromQueue = useCallback(async (id: string) => {
    // Zugehörige Blobs (falls vorhanden) VOR dem Entfernen aus IndexedDB nachschlagen -
    // itemsRef ist der stabile Spiegel von `items` (siehe Doku oben), kein zusätzlicher
    // loadQueue()-Roundtrip nötig.
    const target = itemsRef.current.find((item) => item.id === id);

    // Aus IndexedDB entfernen
    await removeQueueItem(id);
    if (target) {
      await removeQueueBlobs(target.type, target.data);
    }

    // State aktualisieren
    setItems(prevItems => prevItems.filter(item => item.id !== id));
  }, []);

  // Nur den React-State synchronisieren, OHNE erneut in IndexedDB zu schreiben/löschen - für
  // Aufrufer, die eine Queue-Item-Entfernung bereits selbst atomar außerhalb dieses Contexts
  // durchgeführt haben (z.B. queueService.removeStaleQueueItem, das Lesen+Vergleichen+Löschen
  // innerhalb eines ID-Locks kapselt) und danach nur noch Badge/Queue-Liste aktuell halten
  // wollen. PR-Review-Finding (4. Re-Review zu #976): ein erneuter removeFromQueue-Aufruf hier
  // würde `removeQueueItem(id)` ein zweites Mal - diesmal UNGESCHÜTZT, außerhalb des Locks -
  // ausführen und könnte damit einen zwischenzeitlich (nach Freigabe des Locks) neu
  // eingereihten, nie gesendeten Payload für dieselbe ID wieder löschen.
  const forgetQueueItem = useCallback((id: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
  }, []);

  // ✅ FIX: Context-Value mit useMemo wrappen (verhindert neue Objekt-Referenz bei jedem Render)
  // Warum: React Context nutzt Object.is() für Vergleiche. Ohne useMemo wird bei jedem
  // Provider-Render ein neues Objekt erstellt → alle Consumer re-rendern, auch wenn
  // items.length identisch ist. Bei schnellen State-Updates kann Badge zwischen zwei
  // Batches evaluiert werden und sieht temporär inkonsistente Werte.
  const contextValue: QueueContextType = useMemo(() => ({
    items,
    addToQueue,
    removeFromQueue,
    forgetQueueItem,
    confirmPendingItem,
    isProcessing,
    isOnline,
    syncQueue,
    syncStatus: {
      pending: items.length,
      lastSync,
      error: syncError,
      state: syncState,
      lastResult
    }
  }), [items, addToQueue, removeFromQueue, forgetQueueItem, confirmPendingItem, isProcessing, isOnline, syncQueue, lastSync, syncError, syncState, lastResult]);

  return (
    <QueueContext.Provider value={contextValue}>
      {children}
    </QueueContext.Provider>
  );
};

// Hook für einfachen Zugriff
export const useQueue = () => useContext(QueueContext);
